import { Skeleton } from "@gigpro/ui/Skeleton";
import { cn } from "@gigpro/ui/tailwind/cn";
import { useStafferGigsTemplatesPartialUpdate } from "@sidegigllc/api/client";
import { useQuery } from "@tanstack/react-query";
import { WagePicker, type WagePickerProps } from "domains/postGig/components/WagePicker";
import { gigTemplatesQuery } from "domains/postGig/containers/PositionSelect/queries";
import { skillsQuery } from "domains/postGig/containers/WagePicker/queries";
import type { SectionPros } from "domains/postGig/providers/PostGigData/types";
import { useFeatureFlag } from "providers/LaunchDarkly";

export interface WagePickerContainerProps extends Omit<
  WagePickerProps,
  "avgWage" | "recommendedWage" | "templateWage" | "value" | "onValueChange" | "recommendationRefresh"
> {
  value: SectionPros[number]["wageOverride"];
  onValueChange: (value: NonNullable<SectionPros[number]["wageOverride"]>) => void;
  template?: {
    id?: number;
    locationId?: number;
    wage: number;
  };
  skillId: number;
  /** In place of the skill's: last time's wage, for a role from the location's own positions. */
  recommendedWage?: number;
}

export function WagePickerContainer({
  skillId,
  template,
  className,
  onValueChange,
  recommendedWage,
  ...props
}: Readonly<WagePickerContainerProps>) {
  const savePromptEnabled = useFeatureFlag("fe-post-save-changes-prompt");
  const recommendationRefresh = useFeatureFlag("wage-recommendation-refresh");
  const skills = useQuery(skillsQuery());
  const selectedSkill = skills.data?.find(({ id }) => id === skillId);

  const templates = useQuery({
    ...gigTemplatesQuery({ location: { id: template?.locationId ?? -1 } }),
    enabled: !!template?.locationId,
  });
  const updateTemplate = useStafferGigsTemplatesPartialUpdate({ mutation: { onSuccess: () => templates.refetch() } });

  if (!selectedSkill) return <Skeleton className={cn("h-14.5 w-full scale-y-60 self-center", className)} />;

  // With the prompt on, a custom wage never writes back silently: single-shift asks on Continue, multi-shift keeps it per shift.
  const onChange: WagePickerProps["onValueChange"] = (value) => {
    onValueChange({ ...value, skill: selectedSkill });
    if (!savePromptEnabled && template?.id && value.key === "custom" && value.wage !== template.wage) {
      updateTemplate.mutate({ id: template.id, data: { hourly_rate: value.wage } });
    }
  };
  return (
    <WagePicker
      key={skillId}
      className={className}
      onValueChange={onChange}
      helperText={
        !savePromptEnabled && template?.id ? "Editing the default wage will update the position's wage" : undefined
      }
      {...props}
      recommendationRefresh={recommendationRefresh}
      templateWage={template?.wage}
      recommendedWage={recommendedWage ?? selectedSkill.recommended_wage_amount ?? 0}
      avgWage={selectedSkill.average_wage_amount ?? 0}
    />
  );
}
